// Quelle: /opt/webgis/frontend/src/lib/ladephase.ts (Stand 2026-09-29)
// ============================================================
// Der Torwaechter des Ladewerks — EINE Quelle fuer alle Ladezustaende.
//
// Warum ueberhaupt ein Hook: bisher hat jeder Bildschirm selbst entschieden,
// wann er ein Skelett zeigt, und jeder hat es anders getaktet. Ergebnis waren
// Spinner, die 80 ms aufblitzen, und Skelette, die 3 s stehen bleiben, obwohl
// laengst Inhalt da war. Ab hier gibt es genau eine Uhr, und die Bildschirme
// fragen sie nur noch ab.
//
// Die Latenzleiter, gegen die hier gerechnet wird:
//   0-400 ms   gar kein Zustand — Menschen lesen das als "sofort"
//   500 ms     Verzoegerungstor, aber NUR wenn schon passender Inhalt steht
//   1200 ms    erst jetzt Silhouette statt gehaltenem Inhalt
//   2000 ms    ab hier ist ein Loader zulaessig — UND nur bei unbekannter
//              Endstruktur; beides muss zusammenkommen
//   10 s       Worte dazu ("dauert laenger"), Geometrie bleibt gleich
//   30 s       ohne Fortschrittsereignis in den Fehlerzustand
// Quer dazu: 300 ms Mindeststandzeit. Was einmal sichtbar war, bleibt es so
// lange — bei JEDEM Wechsel, nicht nur beim Ausstieg nach "ruhe".
// ============================================================
import { useEffect, useMemo, useRef, useState } from "react";

/** Die acht Zustaende, mehr gibt es nicht. */
export type Ladephase =
  /** nichts laeuft */
  | "ruhe"
  /** laeuft, aber unter dem Tor: bewusst KEIN sichtbarer Zustand */
  | "still"
  /** passender Inhalt steht und wird abgeblendet gehalten */
  | "gehalten"
  /** Textur statt Inhalt */
  | "silhouette"
  /** Endstruktur unbekannt UND lange Wartezeit */
  | "loader"
  /** bezifferbarer Fortschritt liegt vor */
  | "band"
  /** ueber 10 s unterwegs — das Bauteil ergaenzt eine Textzeile */
  | "langsam"
  /** 30 s ohne Fortschrittsereignis */
  | "fehler";

/** Welle der Enthuellung: 1 Grundstruktur, 2 Text und Zahlen, 3 Bilder/Diagramme/Karte. */
export type Welle = 1 | 2 | 3;

// ——— Die Zahlen der Leiter. Nur hier, nirgends abgeschrieben. ———————————
/** Verzoegerungstor: so lange bleibt ein Ladevorgang mit stehendem Inhalt stumm. */
export const TOR_MS = 500;
/**
 * Mindeststandzeit jedes eingeblendeten Zustands. 300 statt der sonst
 * ueblichen 200, weil React seine Suspense-Enthuellungen ohnehin mit
 * SUSPENSE_FALLBACK_THROTTLE_MS = 300 ms taktet — ein kuerzerer Wert wird
 * an einer Suspense-Grenze nie erreicht und erzeugt nur zwei Wahrheiten.
 */
export const MINDESTSTAND_MS = 300;
/** Ab hier weicht gehaltener Inhalt der Silhouette. */
export const SILHOUETTE_MS = 1200;
/** Erwartete Wartezeit, ab der ein Loader ueberhaupt erlaubt ist. */
export const LOADER_MS = 2000;
/** Ab hier Worte statt Textur. */
export const LANGSAM_MS = 10000;
/** So lange darf kein Fortschrittsereignis ausbleiben. */
export const FEHLER_MS = 30000;

/** Versatz zwischen zwei Wellen. */
export const WELLEN_VERSATZ_MS = 90;
/** Staffelung innerhalb einer Welle, je Element. */
export const STAFFEL_MS = 40;
/**
 * Harte Deckelung der Staffelung: ACHT Stufen, also 0 bis 7 mal 40 ms
 * (0 ... 280 ms). Alles ab dem neunten Element faellt auf die letzte Stufe
 * und kommt mit dem achten zusammen — bei 20 Zeilen a 40 ms waeren das sonst
 * 800 ms kuenstliche Verzoegerung fuer Daten, die schon im Speicher liegen.
 *
 * ZWEITER ORT: dieselbe Deckelung steht in base.css an `.stagger`
 * (nth-child(n+9) -> animation-delay .28s). Wer die Zahl hier aendert, aendert
 * sie dort mit — sonst laufen die beiden Fassungen der gleichen Staffelung
 * auseinander, und niemand sieht es, weil beide fuer sich plausibel wirken.
 * Gedeckelt heisst gedeckelt, NICHT abgeschaltet: die ersten acht Elemente
 * behalten ihre Stufen.
 */
export const STAFFEL_MAX = 8;
/** Einblenddauer je Element. */
export const EINBLENDEN_MS = 180;
/** Ausblenddauer der Silhouette — kuerzer als das Einblenden, damit nichts durchblitzt. */
export const AUSBLENDEN_MS = 120;
/** Die Kurve der Enthuellung. Als Zeichenkette, weil sie auch ins CSS geht. */
export const ENTHUELLUNG_KURVE = "cubic-bezier(0.22, 0.65, 0.3, 1)";
/** Opazitaet, auf die gehaltener Inhalt abgeblendet wird. */
export const ABBLENDE_OPAZITAET = 0.55;

/**
 * Verzoegerung eines Elements im Stroemen: Welle plus gedeckelte Staffelung.
 * Wird von <Enthuellung> vergeben und kann von Aufrufern direkt benutzt
 * werden, die ihre Elemente selbst einblenden (Tabellenzeilen, Kacheln).
 */
export function stroemVerzoegerung(welle: Welle, index = 0): number {
  const wellenTeil = (welle - 1) * WELLEN_VERSATZ_MS;
  // STAFFEL_MAX ist die Anzahl der Stufen, nicht die hoechste Stufe: bei
  // 0-basiertem Index ist 7 die letzte von acht. Mit `Math.min(index, 8)`
  // gaebe es neun Stufen (0 ... 320 ms) und die CSS-Fassung passte nicht mehr.
  const stufe = Math.min(Math.max(index, 0), STAFFEL_MAX - 1);
  return wellenTeil + stufe * STAFFEL_MS;
}

/** Was der Aufrufer ueber seine Wartezeit weiss. */
export interface LadephaseEingang {
  /** Laeuft gerade etwas? */
  laedt: boolean;
  /** Steht bereits passender Inhalt (Nachladen, Filterwechsel)? Dann wird gehalten statt ersetzt. */
  hatInhalt?: boolean;
  /** Erwartete Wartezeit in ms, falls bekannt (Import, Export, Erreichbarkeit). */
  erwartet?: number;
  /** Gibt es bezifferbaren Fortschritt? Dann Band statt Textur. */
  kannFortschritt?: boolean;
  /** Zeitstempel des letzten Fortschrittsereignisses — stellt die 30-s-Uhr zurueck. */
  fortschrittTakt?: number;
  /**
   * Aendert sich die Struktur (anderes Projekt, anderer Reiter)? Nur dann
   * loest gehaltener Inhalt nach 1200 ms in eine Silhouette auf. Bleibt die
   * Struktur gleich (Sortierung, Seitenwechsel), wird weiter gehalten.
   */
  strukturwechsel?: boolean;
  /**
   * Weiss der Aufrufer, WIE das Ergebnis aussehen wird — gibt es also eine
   * massgetreue Silhouette? Vorgabe true, denn fast jeder Bildschirm kennt
   * seine eigene Form.
   *
   * Das ist NICHT dasselbe wie hatInhalt. Ein Dashboard auf schmaler Leitung
   * hat noch keinen Inhalt, kennt aber seine Kacheln — es bekommt die
   * Silhouette, auch bei erwartet = 2500. Der erste Kartenaufbau kennt seine
   * Endform dagegen nicht (Ausschnitt, Ebenen, Legende stehen erst nach der
   * Antwort fest) und darf einen Loader zeigen, obwohl die alte Karte noch
   * steht und hatInhalt true meldet. Ein Loader ist nur erlaubt, wo beides
   * zusammenkommt: unbekannte Endstruktur UND lange erwartete Wartezeit.
   */
  strukturBekannt?: boolean;
}

/** Fertige Attribute fuer Hilfstechnik — direkt in das JSX spreizen. */
export interface LadeAria {
  "aria-busy"?: true;
  "aria-live"?: "polite" | "assertive";
  role?: "status" | "alert";
}

export interface LadephaseErgebnis {
  /** Was gezeigt werden soll. */
  phase: Ladephase;
  /**
   * Die Phase OHNE die beiden Uhren. Waehrend phase "langsam" oder "fehler"
   * ist, sagt basis weiterhin, welche Textur darunter steht — sonst wuesste
   * das Bauteil nach 10 s nicht mehr, ob es Silhouette oder Loader zeichnet.
   */
  basis: Ladephase;
  /** Wie lange die aktuelle Phase schon steht, in ms. */
  seit: number;
  /** true = gehaltenen Inhalt auf ABBLENDE_OPAZITAET abblenden. */
  abgeblendet: boolean;
  /** aria-busy / role / aria-live, damit kein Aufrufer sie vergisst. */
  aria: LadeAria;
}

interface Lage {
  hatInhalt: boolean;
  erwartet: number;
  kannFortschritt: boolean;
  strukturwechsel: boolean;
  strukturBekannt: boolean;
}

/**
 * Welche TEXTUR tritt an die Stelle des Inhalts, wenn der Baum an diesem
 * Punkt etwas zeigen will? Drei Kandidaten, in dieser Reihenfolge:
 *
 *   band        bezifferbarer Fortschritt liegt vor. Das Band ersetzt die
 *               TEXTUR, nicht den gehaltenen Inhalt — deshalb steht es hier
 *               und nicht ganz oben im Baum. Frueher stand `kannFortschritt`
 *               vor der Torpruefung: ein Vorgang, der nach 120 ms fertig war,
 *               liess dann ein Band aufblitzen, und gehaltener Inhalt wurde
 *               nicht mehr abgeblendet, weil basis nie "gehalten" wurde.
 *   loader      nur wo beides zutrifft: Endstruktur unbekannt UND erwartete
 *               Wartezeit ueber 2 s. Ein Loader ist die schlechteste Auskunft,
 *               die wir haben; er ist die Ausnahme, nicht der Standardfall.
 *   silhouette  der Normalfall.
 */
function textur(lage: Lage): Ladephase {
  if (lage.kannFortschritt) return "band";
  if (!lage.strukturBekannt && lage.erwartet >= LOADER_MS) return "loader";
  return "silhouette";
}

/**
 * Der Entscheidungsbaum als reine Funktion — testbar und ohne Uhr.
 * Reihenfolge ist Absicht: erst die Frage, ob es ueberhaupt etwas zu halten
 * gibt, dann Tor und Haltefrist, und erst am Ende, welche Textur einspringt.
 */
function basisphase(verstrichen: number, lage: Lage): Ladephase {
  // Nichts zu halten: Textur SOFORT, ohne Tor. Das Tor schuetzt stehenden
  // Inhalt vor unnoetigem Austausch — wo keiner steht, schuetzt es nichts
  // und liesse nur eine leere Flaeche zurueck.
  if (!lage.hatInhalt) {
    /* Ein Band gehoert nicht ins ERSTE Bild. Es ist eine Aussage ueber einen
       Vorgang, keine Vorschau auf den Inhalt — ein Vorgang, der nach 120 ms
       fertig ist, liesse es sonst aufblitzen (gefunden in der Abnahme: schon
       der Anfangszustand rief basisphase(0, …) und bekam "band").
       Die Silhouette dagegen MUSS sofort stehen: hier ist Fall K, und ein Tor
       waere eine weisse Flaeche. Vor dem Tor gilt deshalb die gewoehnliche
       Textur, erst danach darf das Band uebernehmen. */
    if (lage.kannFortschritt && verstrichen < TOR_MS) return textur({ ...lage, kannFortschritt: false });
    return textur(lage);
  }
  if (verstrichen < TOR_MS) return "still";
  if (verstrichen < SILHOUETTE_MS || !lage.strukturwechsel) return "gehalten";
  return textur(lage);
}

function ariaFuer(phase: Ladephase): LadeAria {
  switch (phase) {
    case "ruhe":
      return {};
    case "fehler":
      return { role: "alert", "aria-live": "assertive" };
    case "loader":
    case "band":
    case "langsam":
      // Diese drei tragen Text — sie duerfen und sollen angesagt werden.
      return { "aria-busy": true, role: "status", "aria-live": "polite" };
    default:
      // Silhouette, gehaltener Inhalt und die stille Phase sagen nichts an:
      // eine vorgelesene Textur ist nur Laerm.
      return { "aria-busy": true };
  }
}

interface Stand {
  phase: Ladephase;
  basis: Ladephase;
  /** Zeitstempel, seit dem diese Phase steht. */
  seit: number;
}

/**
 * Der Torwaechter. Alle Zeitgeber liegen hier, alle werden aufgeraeumt, und
 * nach dem Aushaengen wird kein Zustand mehr gesetzt.
 */
export function useLadephase(eingang: LadephaseEingang): LadephaseErgebnis {
  const {
    laedt,
    hatInhalt = false,
    erwartet = 0,
    kannFortschritt = false,
    fortschrittTakt = 0,
    strukturwechsel = true,
    strukturBekannt = true,
  } = eingang;

  // Phase, Basis und Standbeginn liegen in EINEM Zustand: liefen sie
  // auseinander, zeigte seit die Standzeit der falschen Phase an.
  const [stand, setStand] = useState<Stand>(() => {
    if (!laedt) return { phase: "ruhe", basis: "ruhe", seit: Date.now() };
    // Schon beim ersten Bild richtig entscheiden. Erst im Effekt zu starten
    // hiesse: ein Bild lang nichts, dann springt die Silhouette herein.
    const b = basisphase(0, { hatInhalt, erwartet, kannFortschritt, strukturwechsel, strukturBekannt });
    return { phase: b, basis: b, seit: Date.now() };
  });

  // Renderspiegel: der Effekt muss die Standzeit lesen, ohne sich selbst
  // dadurch neu zu planen (sonst setzt jede Phase ihre eigene Uhr zurueck).
  const standRef = useRef(stand);
  standRef.current = stand;

  const lebtRef = useRef(true);
  const beginnRef = useRef(0);
  const taktRef = useRef(0);

  // Bewusst VOR dem Hauptefekt: beim erneuten Einhaengen (StrictMode) muss
  // lebt wieder true sein, bevor der Hauptefekt etwas setzen will.
  useEffect(() => {
    lebtRef.current = true;
    return () => {
      lebtRef.current = false;
    };
  }, []);

  useEffect(() => {
    // Zwei getrennte Uhren, mit Absicht: die Planungsuhr laeuft zur naechsten
    // Schwelle der Leiter, die Halteuhr sitzt einen ausstehenden Wechsel aus.
    // Lagen sie auf einer Variablen, ueberschriebe die naechste Planung den
    // wartenden Wechsel und die Mindeststandzeit fiele still aus.
    let uhr: ReturnType<typeof setTimeout> | undefined;
    let halteUhr: ReturnType<typeof setTimeout> | undefined;
    const raeumen = () => {
      if (uhr !== undefined) clearTimeout(uhr);
      if (halteUhr !== undefined) clearTimeout(halteUhr);
      uhr = undefined;
      halteUhr = undefined;
    };
    // Sichtbar = jemand hat es gesehen. "ruhe" zeigt nichts, "still" ist der
    // bewusst leere Zustand unter dem Tor — beide duerfen sofort weichen.
    const sichtbar = (p: Ladephase) => p !== "ruhe" && p !== "still";
    const setzen = (phase: Ladephase, basis: Ladephase) => {
      if (!lebtRef.current) return;
      const jetzt = standRef.current;
      if (jetzt.phase === phase && jetzt.basis === basis) return;
      // Die Mindeststandzeit gilt fuer JEDEN Wechsel weg von einer sichtbaren
      // Phase, nicht nur fuer den Ausstieg nach "ruhe". Sonst entsteht genau
      // der Aufblitzer, den die Leiter verhindern soll: Silhouette bei 0 ms,
      // nach 100 ms trifft placeholderData ein, hatInhalt kippt auf true, der
      // Baum sagt "still" (unter dem Tor) — und die Silhouette verschwindet
      // nach 100 ms wieder. Ausstieg wie Wechsel warten die Restzeit ab.
      const rest = sichtbar(jetzt.phase) ? MINDESTSTAND_MS - (Date.now() - jetzt.seit) : 0;
      if (rest > 0) {
        if (halteUhr !== undefined) clearTimeout(halteUhr);
        halteUhr = setTimeout(() => {
          halteUhr = undefined;
          setzen(phase, basis);
        }, rest);
        return;
      }
      if (halteUhr !== undefined) {
        clearTimeout(halteUhr);
        halteUhr = undefined;
      }
      setStand((alt) => (alt.phase === phase && alt.basis === basis ? alt : { phase, basis, seit: Date.now() }));
    };

    if (!laedt) {
      beginnRef.current = 0;
      taktRef.current = 0;
      // Die Standzeitpruefung des Ausstiegs steckt jetzt in setzen() — hier
      // wird nur noch gesagt, wohin.
      setzen("ruhe", "ruhe");
      return raeumen;
    }

    if (beginnRef.current === 0) {
      beginnRef.current = Date.now();
      taktRef.current = beginnRef.current;
    }
    // Jedes neue Fortschrittsereignis stellt die 30-s-Uhr zurueck.
    if (fortschrittTakt > taktRef.current) taktRef.current = fortschrittTakt;

    const lage: Lage = { hatInhalt, erwartet, kannFortschritt, strukturwechsel, strukturBekannt };

    const planen = () => {
      const jetzt = Date.now();
      const verstrichen = jetzt - beginnRef.current;
      const seitTakt = jetzt - taktRef.current;
      const basis = basisphase(verstrichen, lage);
      let phase: Ladephase = basis;
      if (seitTakt >= FEHLER_MS) phase = "fehler";
      else if (verstrichen >= LANGSAM_MS) phase = "langsam";
      setzen(phase, basis);
      // Fehler ist ein Endzustand — ab hier laeuft keine Uhr mehr.
      if (phase === "fehler") return;

      // Die Uhr laeuft immer nur bis zur naechsten Schwelle, nicht im Sekundentakt.
      const marken: number[] = [taktRef.current + FEHLER_MS];
      // TOR_MS gilt fuer BEIDE Zweige von basisphase(), nicht nur fuer "still".
      // Vorher stand hier `basis === "still"` — das trifft nur den Fall MIT
      // gehaltenem Inhalt (Zeile 212). Ohne gehaltenen Inhalt haengt das Tor an
      // Zeile 209 (kannFortschritt: vor dem Tor gewoehnliche Textur, danach das
      // Band), und fuer diesen Fall wurde die Marke nie eingeplant: das Band kam
      // erst, wenn zufaellig eine andere Marke feuerte (LANGSAM_MS nach 10 s oder
      // FEHLER_MS nach 30 s) — also praktisch nie. Die Bedingung gehoert an die
      // Zeit, nicht an eine der beiden Auspraegungen.
      if (verstrichen < TOR_MS) marken.push(beginnRef.current + TOR_MS);
      if (basis === "gehalten" && strukturwechsel) marken.push(beginnRef.current + SILHOUETTE_MS);
      if (verstrichen < LANGSAM_MS) marken.push(beginnRef.current + LANGSAM_MS);
      const naechste = Math.min(...marken);
      uhr = setTimeout(planen, Math.max(16, naechste - jetzt));
    };
    planen();
    return raeumen;
  }, [laedt, hatInhalt, erwartet, kannFortschritt, fortschrittTakt, strukturwechsel, strukturBekannt]);

  const aria = useMemo(() => ariaFuer(stand.phase), [stand.phase]);

  return {
    phase: stand.phase,
    basis: stand.basis,
    seit: Date.now() - stand.seit,
    abgeblendet: stand.basis === "gehalten",
    aria,
  };
}
